import { useRef, useEffect, Suspense, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { connect } from 'react-redux';

import * as tracking from 'dibs-tracking';
import { getAbTestTrackingData } from 'dibs-buyer-layout/exports/clientABTestHelpers';
import classNames from 'classnames';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

// components
import { PdpRespPreviewMessageBanner } from '../PdpRespPreviewMessageBanner/PdpRespPreviewMessageBanner';
import { PdpRespPrintHeader } from '../PdpRespPrintHeader/PdpRespPrintHeader';
import { PdpRespSimilarItemsLanding } from '../PdpRespSimilarItems/PdpRespSimilarItemsLanding';
import { PdpRespBreadcrumbs } from '../PdpRespBreadcrumbs/PdpRespBreadcrumbs';
import { PdpRespHeroCarousel } from '../PdpRespHeroCarousel/PdpRespHeroCarousel';
import { PdpRespHeroCarouselPrint } from '../PdpRespHeroCarousel/PdpRespHeroCarouselPrint';
import { PdpRespTitle } from '../PdpRespTitle/PdpRespTitle';
import { PdpSharedSidebarFavorites } from '../PdpSharedSidebarFavorites/PdpSharedSidebarFavorites';
import { PriceDisplay } from 'dibs-buyer-price';
import { PdpRespSidebarButtons } from '../PdpRespSidebarButtons/PdpRespSidebarButtons';
import { PdpRespSidebarBlurbs } from '../PdpRespSidebarBlurbs/PdpRespSidebarBlurbs';
import { PdpRespAboutTabs } from '../PdpRespAboutTabs/PdpRespAboutTabs';
import { PdpRespAboutModule } from '../PdpRespAboutModule/PdpRespAboutModule';
import { PdpRespShippingReturns } from '../PdpRespDetails/PdpRespShippingReturns';
import { PdpRespBuyerAssuranceBox } from '../PdpRespBuyerAssuranceBox/PdpRespBuyerAssuranceBox';
import { PdpRespStorytellingModule } from '../PdpRespStorytellingModule/PdpRespStorytellingModule';
import { PdpRespSidebarSellerInfo } from '../PdpRespSidebarSellerInfo/PdpRespSidebarSellerInfo';
import { PdpRespSidebarContact } from '../PdpRespSidebarContact/PdpRespSidebarContact';
import { PdpRespSimilarItemsFooter } from '../PdpRespSimilarItems/PdpRespSimilarItemsFooter';
import { PdpRespYMALItemsCarousel } from '../PdpRespProductTileCarousels/PdpRespYMALItemsCarousel';
import { PdpRespPersonalizedItemsCarousel } from '../PdpRespProductTileCarousels/PdpRespPersonalizedItemsCarousel';
import { PdpRespSimilarItemsCarousel } from '../PdpRespProductTileCarousels/PdpRespSimilarItemsCarousel';
import { PdpRespDealerDetails } from '../PdpRespDealerDetails/PdpRespDealerDetails';
import { PdpSharedQuantitySelect } from '../PdpSharedButtons/PdpSharedQuantitySelect';
import { PdpSharedReminderLoader } from '../PdpSharedReminder/PdpSharedReminderLoader';
import { PdpSharedRecentHistoryTracker } from '../PdpSharedRecentHistoryTracker/PdpSharedRecentHistoryTracker';
import { PdpSharedPromoCode } from '../PdpSharedPromoCode/PdpSharedPromoCode';
import { PdpSharedRestrictedMaterialsLoader } from '../PdpSharedRestrictedMaterials/PdpSharedRestrictedMaterialsLoader';
import { PdpRespQrCode } from '../PdpRespQrCode/PdpRespQrCode';
import { PdpRespSwatchSelector } from '../PdpRespSwatchSelector/PdpRespSwatchSelector';
import { PdpSharedFramingStatusLazy } from '../PdpSharedFramingStatus/PdpSharedFramingStatusLazy';
import { PdpRespDealerChatLoader } from '../PdpRespDealerChat/PdpRespDealerChatLoader';
import { SharedRespLinksBlock } from '../../shared/SharedRespLinksBlock/SharedRespLinksBlock';
import { PdpSharedEditorialArticles } from '../PdpSharedEditorialArticles/PdpSharedEditorialArticles';
import { PdpSharedAuctionInfo } from '../PdpSharedAuctionInfo/PdpSharedAuctionInfo';
import { PdpRespTopModule } from '../PdpRespSoldRedesign/PdpRespTopModule/PdpRespTopModule';
import { PdpRespTopYMAL } from '../PdpRespSoldRedesign/PdpRespTopYMAL/PdpRespTopYMAL';
import { PdpRespDetailsTabs } from '../PdpRespSoldRedesign/PdpRespDetailsTabs/PdpRespDetailsTabs';
import { PdpRespSellerItems } from '../PdpRespSoldRedesign/PdpRespSellerItems/PdpRespSellerItems';
import { PdpRespAuctionUrgencyInfoLazy } from '../PdpRespAuctionUrgencyInfo/PdpRespAuctionUrgencyInfoLazy';
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy';
import { PdpRespSidebarSellerInfoAndContactLazy } from '../PdpRespSidebarSellerInfo/PdpRespSidebarSellerInfoAndContactLazy';
import { PdpRespSimilarAuctionItemsLazy } from '../PdpRespSimilarAuctionItems/PdpRespSimilarAuctionItemsLazy';
import { PdpSharedAuctionCountdownLazy } from '../PdpSharedAuctionCountdown/PdpSharedAuctionCountdownLazy';
import { KlarnaOnSiteMessage } from 'dibs-klarna/exports/KlarnaOnSiteMessage';
import { PdpRespSellerItemsCarousel } from '../PdpRespProductTileCarousels/PdpRespSellerItemsCarousel';

// callouts
import { NOTIFICATION_KEYS as SHARED_NOTIFICATION_KEYS } from '../PdpSharedNotifications/pdpSharedNotificationConstants';
import { SharedNotificationsWrapper } from '../../shared/SharedNotificationsWrapper';
import { PdpSharedNotificationMultiplePdpVisitsModalLoader } from '../PdpSharedNotifications/PdpSharedNotificationMultiplePdpVisitsModal/PdpSharedNotificationMultiplePdpVisitsModalLoader';
import { getSelectedSku } from '../helpers/getSelectedSku';
import { PDP_SOLD_MORE_FROM_SELLER_LIST } from '../tracking/similarItemsTracking';

// helpers
import classnames from 'classnames';
import { useContactDealerAutoModal } from 'dibs-buyer-layout/exports/contactDealerLauncher';

// context
import { PdpSharedStorytellingTrackingContextProvider } from '../PdpSharedStorytellingComponents/PdpSharedStorytellingTracking';

// styles
import styles from './PdpRespContainer.scss';

/* eslint-disable react/prop-types */
// disable for function as child of PdpRespNotificationsWrapper

export function PdpRespContainerComponent(props) {
    useEffect(() => {
        const makeOfferTrackingData = getAbTestTrackingData(
            document.cookie,
            'REQUIRED_MESSAGE_WITH_OFFER'
        );
        if (makeOfferTrackingData?.testGroup) {
            tracking.trackAbTestEvent(makeOfferTrackingData, 'PDP view');
        }

        const siderailRedesignTrackingData = getAbTestTrackingData(
            document.cookie,
            'PDP_DESKTOP_SIDERAIL_REDESIGN'
        );
        if (siderailRedesignTrackingData?.testGroup) {
            tracking.trackAbTestEvent(siderailRedesignTrackingData, 'pdp_pageview');
        }

        const productTilesRestyleTrackingData = getAbTestTrackingData(
            document.cookie,
            'PDP_PRODUCT_TILES_RESTYLE'
        );
        if (productTilesRestyleTrackingData?.testGroup) {
            tracking.trackAbTestEvent(productTilesRestyleTrackingData, 'pdp_pageview');
        }
    }, []);

    const {
        printWithPrice,
        currency,
        viewer,
        user,
        isPdpRespPreviewPage,
        item,
        displayInfo,
        similarLandingItems,
        relayVariables,
        relay,
        selectedSkuId,
        onSkuChange,
        showAuctionSavePrompt,
        itemSearch,
    } = props;
    const {
        isMultiSku,
        itemId,
        userId,
        isClient,
        isTrade,
        fetchUser,
        fetchAuction,
        isPdpRedesignVariant,
        hasRestyledProductTiles,
    } = relayVariables;
    const selectedSku = getSelectedSku(item, selectedSkuId);
    const { seller, isAuctionEnabled, isSold: isSoldPdp = null, isUnpurchasable, itemIsOnHold } =
        item || {};
    const isSellerAccessible = seller?.isAccessible;
    const isUnavailablePdp = isSoldPdp || isUnpurchasable;
    const [itemQuantity, setItemQuantity] = useState(null);

    const { klarnaOnSiteMessagingEnabled, klarnaNegotiationEnabled, relevantShipping } =
        viewer || {};
    const isNegotiable = !!displayInfo?.ctas?.showMakeOffer;
    const hasShipmentQuote = !!relevantShipping?.shipmentQuote;
    const showKlarnaOnSiteMessage =
        klarnaOnSiteMessagingEnabled &&
        !isTrade &&
        (klarnaNegotiationEnabled || (!isNegotiable && hasShipmentQuote));

    useEffect(() => {
        const trackingData = getAbTestTrackingData(document.cookie, 'DESKTOP_PDP_REDESIGN');
        if (!isSoldPdp) {
            tracking.trackAbTestEvent(trackingData, 'DESKTOP_PDP_REDESIGN');
        }
    }, [isSoldPdp]);

    useContactDealerAutoModal({
        itemId,
        selectedSkuId,
        userId,
        environment: relay.environment,
        currency,
        itemQuantity,
    });

    const scrollToRef = useRef(null);
    const scrollToRefRedesign = useRef(null);

    const includePromoCode = isSellerAccessible && !isUnavailablePdp && !itemIsOnHold;

    const hasEditorialArticles = !!item?.storyTellingModule?.tabs?.[0]?.supplementaryEditorial
        ?.articles.length;

    const enableRecentSalesModuleMakeOfferButton = !!viewer?.enableRecentSalesModuleMakeOfferButton;
    const pdpRareFindTreatment = !!viewer?.pdpRareFindTreatment;

    return (
        <div>
            {/* Preview Page Message Banner */}
            {isPdpRespPreviewPage && <PdpRespPreviewMessageBanner />}

            {/* Print Header */}
            {isClient && <PdpRespPrintHeader itemId={itemId} className={styles.printShow} />}

            {/* Similar Items */}
            <div className={styles.printHide}>
                {isUnavailablePdp ? (
                    <div className={styles.topModuleContainer}>
                        <div className={styles.topModuleContainerInner}>
                            <PdpRespTopModule item={item} user={user} />
                            <div className={styles.divider} />
                            <PdpRespTopYMAL
                                similarItems={similarLandingItems}
                                scrollToRef={scrollToRefRedesign}
                            />
                        </div>
                    </div>
                ) : (
                    <PdpRespSimilarItemsLanding
                        scrollToRef={scrollToRef}
                        item={item}
                        similarItems={similarLandingItems}
                        viewer={viewer}
                        user={user}
                    />
                )}
            </div>

            {/* Notification reminder loader */}
            <PdpSharedReminderLoader
                relayVariables={{
                    userId,
                    fetchUser,
                    itemId,
                    isClient,
                }}
                load={!!user}
            />

            {/* PdpBreadcrumbs */}
            <div className={styles.printHide}>
                <PdpRespBreadcrumbs item={item} />
            </div>

            <div className="rowFlex" data-tn="pdp-main-container">
                <div
                    className={classnames(styles.topContainer, {
                        'colXs12 colLg7 colXl8': !isUnavailablePdp,
                        'colXs12 colLg6 colLgOffset1 colXl6 colXlOffset1': isUnavailablePdp,
                    })}
                >
                    {/* Image Carousel */}
                    <div className={styles.printHide}>
                        <PdpRespHeroCarousel
                            itemTitle={item.title || ''}
                            itemImage={item.firstPhotoWebPath || ''}
                            item={item}
                            selectedSkuId={selectedSkuId}
                            user={user}
                            environment={relay.environment}
                            relayVariables={{
                                userId,
                                fetchUser,
                                itemId,
                                isClient,
                                isTrade,
                            }}
                            viewer={viewer || null}
                            disableZoomModal={isPdpRespPreviewPage}
                            disableRequestMoreImagesModal={isPdpRespPreviewPage}
                            hideBubbles={isUnavailablePdp}
                        />
                    </div>
                    {/* Stripped Image Carousel (print view) */}
                    {isClient && (
                        <div className={styles.printShow}>
                            <PdpRespHeroCarouselPrint item={item} selectedSkuId={selectedSkuId} />
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div
                    className={classnames(styles.topContainer, {
                        'colXs12 colLg5 colXl4': !isUnavailablePdp,
                        'colXs12 colLg4 colXl4': isUnavailablePdp,
                    })}
                >
                    <div
                        className={classnames(styles.sidebarContainer, {
                            [styles.isUnavailablePdp]: isUnavailablePdp,
                        })}
                    >
                        {isAuctionEnabled && <PdpSharedAuctionCountdownLazy item={item} />}

                        <div className="rowFlex">
                            {/* Title */}
                            <div className="colXs12 colLg10">
                                <PdpRespTitle item={item} />
                                {!isAuctionEnabled && (
                                    <div
                                        className={classnames({
                                            [styles.printHide]: !printWithPrice,
                                        })}
                                    >
                                        <PriceDisplay
                                            currency={currency}
                                            item={item}
                                            useSvgIcon
                                            sku={isMultiSku ? selectedSku : null}
                                            appendElementToPrice={
                                                <PdpSharedFramingStatusLazy item={item} />
                                            }
                                            displayVatLabel
                                        />

                                        {!isUnavailablePdp && showKlarnaOnSiteMessage && (
                                            <KlarnaOnSiteMessage
                                                dataKey="credit-promotion-auto-size"
                                                dataLocale="en-US"
                                                item={item}
                                                sku={isMultiSku ? selectedSku : null}
                                                userId={userId}
                                                quantity={itemQuantity}
                                                currency={currency}
                                            />
                                        )}

                                        {includePromoCode && (
                                            <div className={styles.printHide}>
                                                <PdpSharedPromoCode
                                                    displayInfo={displayInfo}
                                                    itemId={itemId}
                                                    viewer={viewer}
                                                    user={user}
                                                    userId={userId}
                                                    currency={currency}
                                                    excludeShippingPromos
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {/* Favorite / Folder */}
                            <div className={`${styles.printHide} colXs2`}>
                                <PdpSharedSidebarFavorites
                                    environment={relay.environment}
                                    item={item}
                                    showHeartCount={viewer.showHeartCount && !isAuctionEnabled}
                                    heartIconWrapperClass={styles.heartIconWrapper}
                                    itemImage={item.firstPhotoWebPath || ''}
                                    isClient={isClient}
                                    userId={userId}
                                    wrapperClass={styles.favoritesWrapper}
                                    showAuctionsTooltip={showAuctionSavePrompt}
                                />
                            </div>
                        </div>

                        {!isAuctionEnabled && isMultiSku && (
                            <PdpRespSwatchSelector
                                currency={currency}
                                isClient={isClient}
                                item={item}
                                user={user}
                                selectedSkuId={selectedSkuId}
                                onClick={skuId => {
                                    onSkuChange(skuId);
                                }}
                                showSwatchDescriptionOnHover
                            />
                        )}

                        {fetchAuction && (
                            <>
                                <PdpSharedAuctionInfo
                                    item={item}
                                    currency={currency}
                                    viewer={viewer}
                                />
                                {isSellerAccessible && (
                                    <div className={styles.printHide}>
                                        <PdpSharedPromoCode
                                            displayInfo={displayInfo}
                                            viewer={viewer}
                                            itemId={itemId}
                                            user={user}
                                            userId={userId}
                                            currency={currency}
                                            excludeShippingPromos
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        {/* Item Ctas */}
                        {isClient && !isAuctionEnabled && (
                            <div className={`${styles.printHide} ${styles.itemCtasWrapper}`}>
                                <PdpSharedQuantitySelect
                                    item={item}
                                    selectedSkuId={selectedSkuId}
                                    setItemQuantity={setItemQuantity}
                                >
                                    {quantitySelected => (
                                        <PdpRespSidebarButtons
                                            currency={currency}
                                            relayVariables={{
                                                userId,
                                                isMultiSku,
                                                itemId,
                                            }}
                                            isClient={isClient}
                                            viewer={viewer}
                                            user={user}
                                            item={item}
                                            displayInfo={displayInfo}
                                            quantitySelected={quantitySelected}
                                            skuId={selectedSkuId}
                                            itemSearch={itemSearch}
                                        />
                                    )}
                                </PdpSharedQuantitySelect>
                            </div>
                        )}

                        {/* Restricted Materials */}
                        {isClient && <PdpSharedRestrictedMaterialsLoader viewer={viewer} />}

                        {/* Auction urgency info */}
                        {isClient && isAuctionEnabled && (
                            <Suspense fallback="">
                                <PdpRespAuctionUrgencyInfoLazy item={item} />
                            </Suspense>
                        )}

                        {/* Blurbs */}
                        {isSellerAccessible && (
                            <PdpRespSidebarBlurbs
                                currency={currency}
                                viewer={viewer}
                                item={item}
                                user={user}
                                displayInfo={displayInfo}
                                sku={selectedSku || null}
                                itemQuantity={itemQuantity}
                            />
                        )}

                        {isClient && isSellerAccessible && (
                            <div className={`${styles.printHide} ${styles.sidebarSection}`}>
                                {/* Questions about this piece? / Contact Dealer */}
                                {isUnavailablePdp ? (
                                    <>
                                        <PdpRespSidebarContact
                                            item={item}
                                            user={user}
                                            itemId={itemId}
                                            userId={userId}
                                            selectedSkuId={selectedSkuId}
                                            itemQuantity={itemQuantity}
                                        />
                                        <div className={styles.sellerInfoContainer}>
                                            <PdpRespSidebarSellerInfo
                                                seller={item.seller}
                                                viewer={viewer}
                                                user={user}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <PdpRespSidebarSellerInfoAndContactLazy
                                        seller={item.seller}
                                        user={user}
                                        item={item}
                                        selectedSkuId={selectedSkuId}
                                        itemQuantity={itemQuantity}
                                    />
                                )}
                            </div>
                        )}
                        <PdpRespQrCode className={styles.printShow} itemId={itemId} />
                    </div>
                </div>
            </div>

            {isClient && isSoldPdp && (
                <div className={styles.printShow}>
                    {/* About */}
                    <PdpRespAboutModule
                        isClient={isClient}
                        item={item}
                        user={user}
                        currency={currency}
                        selectedSkuId={selectedSkuId}
                        viewer={viewer}
                    />

                    {/* Shipping and Returns */}
                    <PdpRespShippingReturns
                        currency={currency}
                        relayVariables={{
                            userId,
                            itemId,
                            isClient,
                            isTrade,
                        }}
                        viewer={viewer}
                        user={user}
                        item={item}
                        itemQuantity={itemQuantity}
                    />
                </div>
            )}

            {isUnavailablePdp ? (
                <div className={styles.printHide}>
                    <PdpRespDetailsTabs
                        selectedSkuId={selectedSkuId}
                        viewer={viewer}
                        user={user}
                        item={item}
                    />
                    {/* Pdp Dealer Chat */}
                    {isClient && <PdpRespDealerChatLoader viewer={viewer} />}
                    {hasRestyledProductTiles ? (
                        <PdpRespSellerItemsCarousel
                            user={user}
                            item={item}
                            viewer={viewer}
                            currency={currency}
                            trackingLocation={PDP_SOLD_MORE_FROM_SELLER_LIST}
                        />
                    ) : (
                        <PdpRespSellerItems viewer={viewer} user={user} item={item} />
                    )}
                </div>
            ) : (
                <>
                    <div className={styles.details}>
                        {isPdpRedesignVariant ? (
                            <PdpRespAboutTabs
                                isClient={isClient}
                                item={item}
                                user={user}
                                currency={currency}
                                selectedSkuId={selectedSkuId}
                                viewer={viewer}
                            />
                        ) : (
                            <PdpRespAboutModule
                                isClient={isClient}
                                item={item}
                                user={user}
                                currency={currency}
                                selectedSkuId={selectedSkuId}
                                viewer={viewer}
                            />
                        )}
                        {/* Pdp Dealer Chat */}
                        {isClient && <PdpRespDealerChatLoader viewer={viewer} />}
                        {/* Shipping and Returns */}
                        <PdpRespShippingReturns
                            currency={currency}
                            relayVariables={{
                                userId,
                                itemId,
                                isClient,
                                isTrade,
                            }}
                            viewer={viewer}
                            user={user}
                            item={item}
                            enableMakeOfferButton={enableRecentSalesModuleMakeOfferButton}
                            showRareFindTreatment={pdpRareFindTreatment}
                            itemQuantity={itemQuantity}
                        />

                        {!isPdpRedesignVariant && (
                            <PdpSharedStorytellingTrackingContextProvider>
                                {/* Storytelling Module */}
                                <div
                                    className={classNames(styles.printHide, {
                                        [styles.storyTellingModulePadding]: !isSellerAccessible,
                                    })}
                                >
                                    <PdpRespStorytellingModule item={item} />
                                </div>
                            </PdpSharedStorytellingTrackingContextProvider>
                        )}
                    </div>

                    {/* Contact Dealer Details */}
                    <div className={styles.printHide}>
                        <PdpRespDealerDetails
                            user={user}
                            item={item}
                            sku={isMultiSku ? selectedSku : null}
                            viewer={viewer}
                            currency={currency}
                            isClient={isClient}
                        />
                    </div>
                </>
            )}

            {/* Similar Items at Auction */}
            <PdpRespSimilarAuctionItemsLazy
                viewer={viewer}
                user={user}
                item={item}
                priceInfoDisplay={displayInfo?.pricing || null}
            />

            {/* You May Also Like */}
            <div ref={scrollToRefRedesign} />
            <div className={styles.printHide}>
                {hasRestyledProductTiles ? (
                    <>
                        <PdpRespYMALItemsCarousel
                            currency={currency}
                            viewer={viewer}
                            user={user}
                            item={item}
                        />
                        <PdpRespPersonalizedItemsCarousel
                            viewer={viewer}
                            user={user}
                            currency={currency}
                        />
                        <PdpRespSimilarItemsCarousel
                            viewer={viewer}
                            user={user}
                            item={item}
                            currency={currency}
                        />
                    </>
                ) : (
                    <PdpRespSimilarItemsFooter
                        currency={currency}
                        viewer={viewer}
                        user={user}
                        item={item}
                        scrollToRef={scrollToRef}
                        relayVariables={relayVariables}
                    />
                )}
            </div>
            <RecentlyViewedItemsLazy
                centerTitle={!hasRestyledProductTiles}
                itemsPerPage={hasRestyledProductTiles ? 5 : 6}
                wrapperClassName={classnames(styles.printHide, styles.recentlyViewed, {
                    [styles.divider]: hasEditorialArticles && viewer.isWordpressArticlesEnabled,
                })}
                authModalLoader={authModalLoader}
            />

            {viewer.isWordpressArticlesEnabled && (
                <>
                    <div className={styles.printHide}>
                        <PdpSharedEditorialArticles item={item} />
                    </div>
                </>
            )}

            {/* Photos - todo (INTL-70: PDP follow on) */}
            <SharedNotificationsWrapper type={SHARED_NOTIFICATION_KEYS.MULTI_PDP_VISITS}>
                {({ isEnabled }) => (
                    <PdpSharedNotificationMultiplePdpVisitsModalLoader
                        isEnabled={isEnabled}
                        itemId={itemId}
                        environment={relay.environment}
                    />
                )}
            </SharedNotificationsWrapper>

            {/* Recent History */}
            {!isPdpRespPreviewPage && <PdpSharedRecentHistoryTracker item={item} />}

            {/* Buyer Assurance Box */}
            <div className={styles.printHide}>
                <PdpRespBuyerAssuranceBox user={user} />
            </div>

            {/* More Ways To Browse */}
            <div className={styles.printHide}>
                <SharedRespLinksBlock
                    viewer={viewer}
                    item={item}
                    links={item.internalBuyLinks}
                    itemSearch={null}
                    onClick={() => {
                        tracking.trackEvent({
                            category: 'product',
                            action: 'more ways to browse link click',
                        });
                    }}
                />
            </div>
        </div>
    );
}

PdpRespContainerComponent.propTypes = {
    currency: PropTypes.string,
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        itemId: PropTypes.string.isRequired,
        isClient: PropTypes.bool.isRequired,
        isMultiSku: PropTypes.bool.isRequired,
        isTrade: PropTypes.bool,
        fetchUser: PropTypes.bool.isRequired,
        followSearchPages: PropTypes.array,
        fetchFollowSearch: PropTypes.bool.isRequired,
        skuId: PropTypes.string,
        isPdpRedesignVariant: PropTypes.bool,
    }).isRequired,
    onSkuChange: PropTypes.func.isRequired,
    relay: PropTypes.object.isRequired,
    selectedSkuId: PropTypes.string,

    // relay response data
    viewer: PropTypes.object.isRequired,
    itemSearch: PropTypes.object,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    displayInfo: PropTypes.object,
    similarLandingItems: PropTypes.object,

    // from redux
    printWithPrice: PropTypes.bool.isRequired,
    openTradeAskSellerTooltip: PropTypes.func,
};

function mapStateToProps({ pdpPrint, pdpPreviewPage, sharedNotificationsReducer, relayVariables }) {
    return {
        printWithPrice: pdpPrint.printWithPrice,
        isPdpRespPreviewPage: pdpPreviewPage.isPdpRespPreviewPage,
        showAuctionSavePrompt: !!sharedNotificationsReducer?.notifications?.[
            SHARED_NOTIFICATION_KEYS.AUCTION_SAVE_PDP_PROMPT
        ],
        relayVariables: relayVariables?.variables,
    };
}

export const PdpRespContainer = createFragmentContainer(
    connect(mapStateToProps)(PdpRespContainerComponent),
    {
        viewer: graphql`
            fragment PdpRespContainer_viewer on Viewer {
                relevantShipping(
                    itemId: $itemId
                    buyerId: $userId
                    quantity: $itemQuantity
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) {
                    shipmentQuote {
                        serviceId
                    }
                }
                klarnaOnSiteMessagingEnabled: isEnabledByLocale(featureName: "KLARNA_OSM")
                klarnaNegotiationEnabled: featureFlag(feature: "klarnaNegotiationFlow")
                isWordpressArticlesEnabled: isEnabledByLocale(featureName: "WP_EDITORIAL")
                enableRecentSalesModuleMakeOfferButton: featureFlag(
                    feature: "recentSalesModuleMakeOfferButton"
                )
                pdpRareFindTreatment: featureFlag(feature: "pdpRareFindTreatment")
                showHeartCount: featureFlag(feature: "pdpDisplayHeartCount")
                ...SharedRespLinksBlock_viewer
                ...PdpRespSidebarBlurbs_viewer
                ...PdpRespSimilarItemsFooter_viewer @skip(if: $hasRestyledProductTiles)
                ...PdpRespYMALItemsCarousel_viewer @include(if: $hasRestyledProductTiles)
                ...PdpRespPersonalizedItemsCarousel_viewer @include(if: $hasRestyledProductTiles)
                ...PdpRespSimilarItemsCarousel_viewer @include(if: $hasRestyledProductTiles)
                ...PdpRespSellerItemsCarousel_viewer
                    @arguments(userCountryCode: $userCountryCode)
                    @include(if: $hasRestyledProductTiles)
                ...PdpRespHeroCarousel_viewer @include(if: $fetchPdp)
                ...PdpRespDealerDetails_viewer
                ...PdpRespDealerChatLoader_viewer @include(if: $isClient)
                ...PdpSharedRestrictedMaterialsLoader_viewer
                ...PdpRespDetailsTabs_viewer
                ...PdpRespSellerItems_viewer @skip(if: $hasRestyledProductTiles)
                ...PdpSharedPromoCode_viewer
                ...PdpRespSimilarAuctionItemsLazy_viewer
                ...PdpSharedAuctionInfo_viewer @include(if: $fetchAuction)
                ...PdpRespSidebarButtons_viewer
            }
        `,
        user: graphql`
            fragment PdpRespContainer_user on User {
                ...PdpRespSimilarItemsFooter_user @skip(if: $hasRestyledProductTiles)
                ...PdpRespYMALItemsCarousel_user @include(if: $hasRestyledProductTiles)
                ...PdpRespPersonalizedItemsCarousel_user @include(if: $hasRestyledProductTiles)
                ...PdpRespSimilarItemsCarousel_user @include(if: $hasRestyledProductTiles)
                ...PdpRespSellerItemsCarousel_user @include(if: $hasRestyledProductTiles)
                ...PdpRespSimilarItemsLanding_user
                ...PdpRespSidebarButtons_user
                ...PdpRespSidebarBlurbs_user
                ...PdpRespShippingReturns_user
                ...PdpRespBuyerAssuranceBox_user
                ...PdpRespSidebarContact_user
                ...PdpRespHeroCarousel_user
                ...PdpRespDealerDetails_user
                ...PdpRespSidebarSellerInfo_user
                ...PdpRespSidebarSellerInfoAndContactLazy_user
                ...PdpSharedPromoCode_user
                ...PdpRespSwatchSelector_user @include(if: $isMultiSku)
                ...PdpRespAboutModule_user
                ...PdpRespAboutTabs_user @include(if: $isPdpRedesignVariant)
                ...PdpRespTopModule_user
                ...PdpRespDetailsTabs_user
                ...PdpRespSellerItems_user @skip(if: $hasRestyledProductTiles)
                ...getRecentHistory_user @relay(mask: false)
                ...PdpRespSimilarAuctionItemsLazy_user
            }
        `,
        item: graphql`
            fragment PdpRespContainer_item on Item {
                serviceId
                firstPhotoWebPath(size: small)
                title
                defaultSkuId
                isSold
                isUnpurchasable
                isAuctionEnabled
                itemIsOnHold: isOnHold(buyerId: $userId)
                ...PdpRespAuctionUrgencyInfo_item @include(if: $fetchAuction)
                ...PdpSharedEditorialArticles_item
                ...PdpRespStorytellingModule_item @skip(if: $isPdpRedesignVariant)
                ...PdpRespSimilarItemsLanding_item
                ...PdpRespBreadcrumbs_item
                ...PdpRespHeroCarouselPrint_item
                ...PdpRespTitle_item
                ...PdpRespSidebarButtons_item
                ...PdpRespSidebarBlurbs_item
                ...PdpRespShippingReturns_item
                ...PdpRespSidebarContact_item
                ...PdpRespSimilarItemsFooter_item @skip(if: $hasRestyledProductTiles)
                ...PdpRespYMALItemsCarousel_item @include(if: $hasRestyledProductTiles)
                ...PdpRespSimilarItemsCarousel_item @include(if: $hasRestyledProductTiles)
                ...PdpRespSellerItemsCarousel_item @include(if: $hasRestyledProductTiles)
                ...PdpSharedQuantitySelect_item
                ...PdpSharedRecentHistoryTracker_item
                ...SharedRespLinksBlock_item
                ...PriceDisplay_item
                    @arguments(
                        userId: $userId
                        isTrade: $isTrade
                        userCountryCode: $userCountryCode
                    )
                ...PdpRespSwatchSelector_item @include(if: $isMultiSku)
                ...PdpSharedFramingStatus_item
                ...PdpRespDealerDetails_item
                ...PdpRespAboutModule_item
                ...PdpRespAboutTabs_item @include(if: $isPdpRedesignVariant)
                ...PdpRespTopModule_item
                ...PdpRespDetailsTabs_item
                ...PdpRespSellerItems_item @skip(if: $hasRestyledProductTiles)
                ...PdpSharedSidebarFavorites_item
                ...PdpSharedAuctionInfo_item @include(if: $fetchAuction)
                ...PdpSharedAuctionCountdownLazy_item
                ...PdpRespSimilarAuctionItemsLazy_item
                ...KlarnaOnSiteMessage_item
                    @arguments(
                        userId: $userId
                        isTrade: $isTrade
                        userCountryCode: $userCountryCode
                    )
                ... @include(if: $fetchPdp) {
                    seller {
                        isAccessible
                        ...PdpRespSidebarSellerInfo_seller
                        ...PdpRespSidebarSellerInfoAndContactLazy_seller
                    }
                    ...PdpRespHeroCarousel_item
                    internalBuyLinks {
                        ...SharedRespLinksBlock_links
                    }
                    ...PdpRespSidebarSellerInfoAndContactLazy_item
                }
                skus {
                    ...PdpRespDealerDetails_sku @include(if: $isMultiSku)
                    serviceId
                    ...PriceDisplay_sku
                        @include(if: $isMultiSku)
                        @arguments(
                            hasSkuId: true
                            userId: $userId
                            isTrade: $isTrade
                            userCountryCode: $userCountryCode
                        )
                    ...PdpRespSidebarBlurbs_sku
                    ...KlarnaOnSiteMessage_sku
                        @include(if: $isMultiSku)
                        @arguments(
                            hasSkuId: true
                            userId: $userId
                            isTrade: $isTrade
                            userCountryCode: $userCountryCode
                        )
                }
                storyTellingModule {
                    tabs {
                        supplementaryEditorial {
                            articles {
                                title
                            }
                        }
                    }
                }
            }
        `,
        displayInfo: graphql`
            fragment PdpRespContainer_displayInfo on ItemDisplayInfo {
                ...PdpRespSidebarButtons_displayInfo
                ...PdpSharedPromoCode_displayInfo
                ...PdpRespSidebarBlurbs_displayInfo
                ctas {
                    showMakeOffer
                }
            }
        `,
        similarLandingItems: graphql`
            fragment PdpRespContainer_similarLandingItems on moreLikeThisItemConnection {
                ...PdpRespSimilarItemsLanding_similarItems
                ...PdpRespTopYMAL_similarItems
            }
        `,
        itemSearch: graphql`
            fragment PdpRespContainer_itemSearch on ItemSearchQueryConnection {
                ...PdpRespSidebarButtons_itemSearch
            }
        `,
    }
);
